import React, { useEffect, useState } from 'react';
import { Leaf, Minus, Plus, Loader2, Trash2 } from 'lucide-react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { Checkbox } from '../ui/checkbox';
import { toast } from 'sonner';
import { easerGet, easerPost } from '../../lib/easerApi';
import { enqueue, shouldQueue, queuedBecause } from '../../lib/offlineQueue';
import { tagKey } from '../../lib/jobTags';

const STEP = 0.5;
const MAX = 30;

/** Green-trade Easers only (Dolibarr user tag « easer green »). */
export const isGreenEaser = (user) => (user?.tags || []).some((t) => tagKey(t) === 'green');

export const fmtVolume = (v) => `${Number(v).toLocaleString('fr-FR', { maximumFractionDigits: 2 })} m³`;

/**
 * « Enlèvement de déchets verts » : asked when a Green Easer ends an intervention (and editable from the slot detail).
 * Ticks « Enlèvement » + volume (m³) on the Dolibarr event, i.e. the « Forfait enlèvement » line of the intervention sheet.
 * `onProceed(volume | null)` is called after the choice; `null` = no removal.
 */
export function EvacuationDialog({ open, ev, onClose, onProceed, onUnauthorized, mode = 'end', existing = null }) {
  const [enabled, setEnabled] = useState(false);
  const [volume, setVolume] = useState(1);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    const has = existing != null && existing > 0;
    setEnabled(has || mode === 'edit');
    setVolume(has ? existing : 1);
  }, [open, existing, mode]);

  const bump = (d) => setVolume((v) => Math.min(MAX, Math.max(STEP, Math.round((v + d) / STEP) * STEP)));

  const submit = async () => {
    const value = enabled ? volume : null;
    setBusy(true);
    try {
      await onProceed(value);
    } finally {
      setBusy(false);
    }
  };

  const title = mode === 'edit' ? "Modifier l'enlèvement" : 'Enlèvement de déchets verts ?';
  const locked = existing != null && existing > 0; // Dolibarr does not allow removing a recorded removal

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o && !busy) onClose(); }}>
      <DialogContent className="max-w-sm" data-testid="evacuation-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Leaf className="h-5 w-5 text-[hsl(var(--job-green))]" /> {title}</DialogTitle>
          <DialogDescription>
            {ev?.label ? <span className="block truncate font-medium text-foreground">{ev.label}</span> : null}
            Coché, l'enlèvement et son volume sont enregistrés sur l'intervention Dolibarr (ligne « Forfait enlèvement »).
          </DialogDescription>
        </DialogHeader>

        <label className={`flex items-center gap-3 rounded-xl border p-3 cursor-pointer ${enabled ? 'border-[hsl(var(--job-green))]/60 bg-[hsl(var(--job-green))]/10' : 'border-border'}`}>
          <Checkbox checked={enabled} disabled={locked} onCheckedChange={(c) => setEnabled(Boolean(c))} data-testid="evacuation-checkbox" />
          <span className="text-sm font-medium">Enlèvement de déchets verts</span>
        </label>

        {enabled && (
          <div className="rounded-xl border border-border p-3" data-testid="evacuation-volume">
            <p className="text-xs text-muted-foreground mb-2">Volume enlevé (m³)</p>
            <div className="flex items-center justify-between gap-2">
              <Button type="button" variant="outline" size="icon" className="h-11 w-11" onClick={() => bump(-STEP)} disabled={volume <= STEP} aria-label="Moins" data-testid="evacuation-minus"><Minus className="h-4 w-4" /></Button>
              <p className="text-2xl font-semibold tabular-nums font-['Space_Grotesk']" data-testid="evacuation-volume-value">{fmtVolume(volume)}</p>
              <Button type="button" variant="outline" size="icon" className="h-11 w-11" onClick={() => bump(STEP)} disabled={volume >= MAX} aria-label="Plus" data-testid="evacuation-plus"><Plus className="h-4 w-4" /></Button>
            </div>
            <div className="flex gap-1.5 mt-2">
              {[0.5, 1, 2, 3, 5].map((q) => (
                <button key={q} type="button" onClick={() => setVolume(q)} className={`flex-1 h-9 rounded-lg border text-xs font-medium ${volume === q ? 'bg-[hsl(var(--job-green))] text-white border-[hsl(var(--job-green))]' : 'border-border hover:bg-secondary'}`} data-testid={`evacuation-quick-${String(q).replace('.', '_')}`}>{q}</button>
              ))}
            </div>
          </div>
        )}
        {locked && <p className="text-xs text-muted-foreground flex items-start gap-1"><Trash2 className="h-3.5 w-3.5 mt-0.5 shrink-0" /> Un enlèvement enregistré ne se retire pas depuis l'application (comme dans Dolibarr) : ajustez le volume si besoin.</p>}

        <div className="flex gap-2 pt-1">
          {mode === 'end' && !locked && <Button type="button" variant="outline" className="flex-1 h-11" onClick={() => { setEnabled(false); onProceed(null); }} disabled={busy} data-testid="evacuation-skip">Sans enlèvement</Button>}
          {mode === 'edit' && <Button type="button" variant="outline" className="flex-1 h-11" onClick={onClose} disabled={busy} data-testid="evacuation-cancel">Annuler</Button>}
          <Button type="button" className="flex-1 h-11 bg-[hsl(var(--job-green))] hover:bg-[hsl(var(--job-green))]/90 text-white" onClick={submit} disabled={busy} data-testid="evacuation-confirm">
            {busy && <Loader2 className="h-4 w-4 mr-1 animate-spin" />}{mode === 'end' ? (enabled ? 'Terminer avec enlèvement' : 'Terminer') : 'Enregistrer'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

/** Send the removal to the backend, or queue it when offline. Returns { queued } or throws on a server refusal. */
export async function saveEvacuation(ev, volume, { onUnauthorized } = {}) {
  try {
    const res = await easerPost(`/api/easer/events/${ev.id}/evacuation`, { volume_m3: volume });
    return { queued: false, data: res.data };
  } catch (err) {
    const status = err?.response?.status;
    if (status === 401) { onUnauthorized && onUnauthorized(); throw err; }
    if (shouldQueue(err)) {
      await enqueue({ type: 'evacuation', eventId: ev.id, label: ev.label, payload: { volume_m3: volume } });
      toast.info(queuedBecause(err, 'enlèvement'));
      return { queued: true };
    }
    toast.error(err.response?.data?.detail || "Enlèvement non enregistré");
    throw err;
  }
}

/** Current removal of a slot (null while loading / when the slot is not linked to an intervention line). */
export function useEvacuation(ev, enabled) {
  const [state, setState] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);
  useEffect(() => {
    if (!enabled || !ev?.id) { setState(null); return; }
    let alive = true;
    easerGet(`/api/easer/events/${ev.id}/evacuation`).then((r) => { if (alive) setState(r.data); }).catch(() => { if (alive) setState({ unavailable: true }); });
    return () => { alive = false; };
  }, [ev?.id, enabled, refreshKey]);
  return [state, () => setRefreshKey((k) => k + 1)];
}
